import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiLoader, FiAlertCircle, FiCheckCircle } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import { telegramLogin } from '../utils/api';
import { useI18n } from '../i18n/useI18n';

/**
 * Telegram 登录回调页面
 */
export function TelegramLoginCallback() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t, setLang } = useI18n();
  const [status, setStatus] = useState('loading'); // loading | success | error
  const [errorMessage, setErrorMessage] = useState('');
  const calledRef = useRef(false);

  useEffect(() => {
    if (calledRef.current) return; // 防止 React StrictMode 开发环境下的二次调用
    calledRef.current = true;

    const handleLogin = async () => {
      try {
        // 从 URL 查询参数获取登录凭证
        const urlParams = new URLSearchParams(window.location.search);
        const userId = urlParams.get('user_id');
        const code = urlParams.get('code');
        const id = urlParams.get('id');

        // 验证参数
        if (!userId || !code || !id) {
          setStatus('error');
          setErrorMessage(t('tgLogin.missingParams'));
          setTimeout(() => {
            navigate('/');
          }, 3000);
          return;
        }

        // 调用登录 API
        const response = await telegramLogin({ user_id: userId, code, id });

        if (response.code === 0 || response.code === 1) {
          // 登录成功
          setStatus('success');

          // 保存登录信息
          login({
            data: response.data,
            user_id: userId,
          });

          // 同步语言偏好（优先使用后端返回的用户语言）
          if (response?.data?.lang) {
            setLang(response.data.lang);
          }

          // 跳转到首页
          setTimeout(() => {
            navigate('/');
          }, 1500);
        } else {
          // 登录失败
          setStatus('error');
          setErrorMessage(response.msg || t('tgLogin.errorTitle'));
          setTimeout(() => {
            navigate('/');
          }, 3000);
        }
      } catch (error) {
        console.error('登录失败:', error);
        setStatus('error');
        setErrorMessage(error.message || t('tgLogin.errorTitle'));
        setTimeout(() => {
          navigate('/');
        }, 3000);
      }
    };

    handleLogin();
  }, [login, navigate]);

  return (
    <div className="min-h-screen app-shell-bg flex items-center justify-center p-6">
      <div className="glass-xl w-full max-w-[480px] p-8 text-center">
        {status === 'loading' && (
          <>
            <FiLoader className="text-5xl text-sky-400 mx-auto mb-4 animate-spin" />
            <h2 className="text-2xl mb-2 text-white">{t('tgLogin.loadingTitle')}</h2>
            <p className="text-slate-300/70 text-sm">{t('tgLogin.loadingDesc')}</p>
          </>
        )}

        {status === 'success' && (
          <>
            <FiCheckCircle className="text-5xl text-emerald-400 mx-auto mb-4" />
            <h2 className="text-2xl mb-2 text-white">{t('tgLogin.successTitle')}</h2>
            <p className="text-slate-300/70 text-sm">{t('tgLogin.successDesc')}</p>
          </>
        )}

        {status === 'error' && (
          <>
            <FiAlertCircle className="text-5xl text-red-400 mx-auto mb-4" />
            <h2 className="text-2xl mb-2 text-white">{t('tgLogin.errorTitle')}</h2>
            <p className="text-slate-300/70 text-sm mb-4">{errorMessage}</p>
            <p className="text-slate-400 text-xs">{t('tgLogin.errorDesc')}</p>
          </>
        )}
      </div>
    </div>
  );
}
